import React, { useState } from 'react';

import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import AdvancedInput from '@portal/components/AdvancedInput/AdvancedInput';
import GenericModal from '@portal/components/GenericModal/GenericModal';
import ThemedText from '@portal/components/ThemedText/ThemedText';
import { translate } from '@portal/services/i18n';
import './CardFirstAccessModal.less';

type CardFirstAccessModalProps = {
  visible: boolean;
  onClose: () => void;
  onConfirm: () => void;
  disabled: boolean;
  myPassword: string | null;
  showCurrentPassword: boolean;
  onToggleCurrentPasswordVisibility: () => void;
  currentPassword: string;
  onCurrentPasswordChange: (value: string) => void;
  newPassword: string;
  onNewPasswordChange: (value: string) => void;
};

const CardFirstAccessModal = ({
  visible,
  onClose,
  onConfirm,
  disabled,
  myPassword,
  showCurrentPassword,
  onToggleCurrentPasswordVisibility,
  currentPassword,
  onCurrentPasswordChange,
  newPassword,
  onNewPasswordChange,
}: CardFirstAccessModalProps) => {
  const [showInputCurrentPassword, setShowInputCurrentPassword] =
    useState(false);
  const [showInputNewPassword, setShowInputNewPassword] = useState(false);

  const handleClose = () => {
    setShowInputCurrentPassword(false);
    setShowInputNewPassword(false);
    onClose();
  };

  return (
    <GenericModal
      visible={visible}
      onClose={handleClose}
      title={translate(
        'PAGES.CARDS.MY_CARDS.DETAILS.UNBLOCK_CARD.FIRST_ACCESS.MODAL.TITLE'
      )}
      description={translate(
        'PAGES.CARDS.MY_CARDS.DETAILS.UNBLOCK_CARD.FIRST_ACCESS.MODAL.DESCRIPTION'
      )}
      actions={[
        {
          label: translate(
            'PAGES.CARDS.MY_CARDS.DETAILS.UNBLOCK_CARD.FIRST_ACCESS.MODAL.CLOSE'
          ),
          onClick: handleClose,
          variant: 'outlined',
        },
        {
          label: translate(
            'PAGES.CARDS.MY_CARDS.DETAILS.UNBLOCK_CARD.FIRST_ACCESS.MODAL.CONFIRM'
          ),
          onClick: onConfirm,
          variant: 'primary',
          disabled,
        },
      ]}
    >
      <div
        className="card-first-access-modal__current-password"
        data-sentry-mask
      >
        <ThemedText variant="h4" className="typo-mb-md">
          {translate(
            'PAGES.CARDS.MY_CARDS.DETAILS.UNBLOCK_CARD.FIRST_ACCESS.MODAL.REMEMBER_CURRENT_PASSWORD'
          )}
        </ThemedText>
        <ThemedText
          variant="h4"
          className="card-first-access-modal__current-password__value"
        >
          {showCurrentPassword
            ? myPassword
            : translate(
                'PAGES.CARDS.MY_CARDS.DETAILS.UNBLOCK_CARD.FIRST_ACCESS.MODAL.KEY'
              )}
        </ThemedText>
        <button
          onClick={onToggleCurrentPasswordVisibility}
          className="transparent-button"
        >
          {showCurrentPassword ? <EyeOutlined /> : <EyeInvisibleOutlined />}
        </button>
      </div>
      <div data-sentry-mask>
        <AdvancedInput
          testId="card-change-password-input"
          label={translate(
            'PAGES.CARDS.MY_CARDS.DETAILS.UNBLOCK_CARD.FIRST_ACCESS.MODAL.CURRENT_PASSWORD_LABEL'
          )}
          type={showInputCurrentPassword ? 'text' : 'password'}
          value={currentPassword}
          maxLength={4}
          helperText={`${currentPassword.length}/4`}
          onChange={onCurrentPasswordChange}
          inputMode="numeric"
          endIcon={
            showInputCurrentPassword ? (
              <EyeOutlined onClick={() => setShowInputCurrentPassword(false)} />
            ) : (
              <EyeInvisibleOutlined
                onClick={() => setShowInputCurrentPassword(true)}
              />
            )
          }
        />
      </div>
      <div data-sentry-mask>
        <ThemedText variant="h4" className="typo-mb-md">
          {translate(
            'PAGES.CARDS.MY_CARDS.DETAILS.UNBLOCK_CARD.FIRST_ACCESS.MODAL.SECOND_DESCRIPTION'
          )}
        </ThemedText>
        <AdvancedInput
          testId="card-change-password-input"
          label={translate(
            'PAGES.CARDS.MY_CARDS.DETAILS.CHANGE_PASSWORD.PASSWORD_LABEL'
          )}
          type={showInputNewPassword ? 'text' : 'password'}
          inputMode="numeric"
          value={newPassword}
          maxLength={4}
          helperText={`${newPassword.length}/4`}
          onChange={onNewPasswordChange}
          endIcon={
            showInputNewPassword ? (
              <EyeOutlined onClick={() => setShowInputNewPassword(false)} />
            ) : (
              <EyeInvisibleOutlined
                onClick={() => setShowInputNewPassword(true)}
              />
            )
          }
        />
      </div>
    </GenericModal>
  );
};

export default CardFirstAccessModal;
